<template>
  <configuration-menu-name title="Umschlag" :menu="menu" max="255" @update:data="$emit('update:menu', $event)">
    <p>
      Gestalten Sie nun den Umschlag Ihrer Gastrokarte mit den wichtigsten Informationen.<br />
      Alle mit * gekennzeichneten Felder sind Pflichtfelder.
    </p>
  </configuration-menu-name>
  <div class="card shadow rounded-corner">
    <div class="card-body">
      <h5 class="card-title text-secondary fw-bold text-uppercase">Deckblatt</h5>
      <p>
        Erstellen Sie Ihr individuelles Deckblatt. Nutzen Sie die optionalen Textfelder „Titel“ und „Untertitel“, indem
        Sie beispielsweise den Namen Ihres Gastronomiebetriebes oder die Bezeichnung Ihrer Karte wie „Speise- und
        Getränkekarte“ einfügen. Ebenso können Sie hier Ihr Logo hochladen.
      </p>

      <base-form-input
        label="Titel"
        name="title"
        max="255"
        :value="menu.attributes.title"
        :errors="menu.errors ? menu.errors.title : null"
        :required="inputRequired('title')"
        @update:data="changeMenuData"
      ></base-form-input>

      <base-form-input
        label="Untertitel"
        name="subtitle"
        max="255"
        :value="menu.attributes.subtitle"
        :errors="menu.errors ? menu.errors.subtitle : null"
        :required="inputRequired('subtitle')"
        @update:data="changeMenuData"
      ></base-form-input>

      <image-upload
        :image-url="menu.attributes.absolute_logo_path"
        :errors="menu.errors ? menu.errors.logo : null"
        @uploadImage="uploadLogo"
        @deleteImage="deleteLogo"
      ></image-upload>
    </div>
  </div>

  <br class="mb-4" />

  <div class="card shadow rounded-corner">
    <div class="card-body">
      <h5 class="card-title text-secondary fw-bold text-uppercase">Kontaktdaten</h5>
      <p>
        Tragen Sie hier Ihre Anschrift ein und teilen Sie Ihren Kund/innen mit, wie sie Sie erreichen können. Diese
        Informationen werden auf der Rückseite Ihrer Gastrokarte abgebildet.
      </p>
      <button class="btn btn-secondary text-white mb-3" @click="getCustomerContact">
        Daten aus Standardadresse übernehmen
      </button>
      <base-address-form
        :address="menu.attributes.data.contact"
        :errors="menu.errors"
        :hide-country="true"
        :show-full-name="true"
        @update:data="changeMenuData"
      ></base-address-form>

      <hr class="mt-0" />

      <div class="input-group mb-3">
        <span class="input-group-text fs-4 text-primary d-flex justify-content-center"
          ><i class="bi bi-telephone"></i
        ></span>
        <base-form-input
          :errors="menu.errors ? menu.errors?.data?.contact?.phone : null"
          :required="inputRequired('phone')"
          :value="menu.attributes ? menu.attributes?.data?.contact?.phone : ''"
          attribute="contact"
          label="Telefonnummer"
          name="phone"
          type="phone"
          @update:data="changeMenuData"
        ></base-form-input>
      </div>
      <div class="input-group mb-3">
        <span class="input-group-text fs-4 text-primary d-flex justify-content-center"
          ><i class="bi bi-envelope"></i
        ></span>
        <base-form-input
          :errors="menu.errors ? menu.errors?.data?.contact?.email : null"
          :required="inputRequired('email')"
          :value="menu.attributes ? menu.attributes?.data?.contact?.email : ''"
          attribute="contact"
          label="E-Mail Adresse"
          name="email"
          type="email"
          @update:data="changeMenuData"
        ></base-form-input>
      </div>
      <div class="input-group">
        <span class="input-group-text fs-4 text-primary d-flex justify-content-center"
          ><i class="bi bi-globe"></i
        ></span>
        <base-form-input
          :errors="menu.errors ? menu.errors?.data?.contact?.homepage : null"
          :required="inputRequired('homepage')"
          :value="menu.attributes ? menu.attributes?.data?.contact?.homepage : ''"
          attribute="contact"
          label="Homepage"
          name="homepage"
          type="url"
          @update:data="changeMenuData"
        ></base-form-input>
      </div>
    </div>
  </div>

  <br class="mb-4" />
  <configuration-businesshours
    :businesshours="
      menu.attributes.data && menu.attributes.data.businesshours ? menu.attributes.data.businesshours : []
    "
    @update:data="changeMenuData"
  ></configuration-businesshours>

  <br class="mb-4" />

  <div class="card shadow rounded-corner">
    <div class="card-body">
      <h5 class="card-title text-secondary fw-bold text-uppercase">Allergene</h5>
      <p class="card-text">
        Gemäß der Europäischen Behörde für Lebensmittelsicherheit müssen sich Kunden über Allergene informieren können.
      </p>
      <select class="form-select p-3" name="info" data-attribute="allergens" @change="changeMenuData">
        <option value="1" :selected="menu.attributes.data?.allergens?.info == 1">verwendete Allergene anzeigen</option>
        <option value="2" :selected="menu.attributes.data?.allergens?.info == 2">
          Hinweis anzeigen: Allergene erfragen Sie bitte bei unserem Personal
        </option>
      </select>
    </div>
  </div>

  <br class="mb-4" />

  <div class="card shadow rounded-corner">
    <div class="card-body">
      <h5 class="card-title text-secondary fw-bold text-uppercase">Zusatzinformationen</h5>
      <p class="card-text">
        Geben Sie hier zusätzliche Informationen an, die an der Umschlagrückseite unter den Öffnungszeiten eingefügt
        werden.
      </p>

      <base-textarea
        :errors="menu.errors ? menu.errors?.data?.additions?.info : null"
        :rows="3"
        :value="menu.attributes ? menu.attributes?.data?.additions?.info : ''"
        attribute="additions"
        label="Zusatzinformationen"
        name="info"
        @update:data="changeMenuData"
      />
    </div>
  </div>

  <br class="mb-4" />
</template>

<script>
import { useVModel } from '@vueuse/core';

import { useRoute } from 'vue-router';

import useCover from '@/composables/useCover';
import ImageUpload from '@/components/ImageUpload';
import ConfigurationBusinesshours from '@/components/ConfigurationBusinesshours';
import ConfigurationMenuName from '@/components/ConfigurationMenuName';
import { getContactByStatus } from '@/api/contacts';

import merge from 'lodash/merge';

export default {
  components: {
    ImageUpload,
    ConfigurationBusinesshours,
    ConfigurationMenuName,
  },
  props: {
    menu: { type: Object, default: null },
  },
  emits: ['update:menu'],
  setup(props, { emit }) {
    const menu = useVModel(props, 'menu', emit);

    const changeMenuData = (param) => {
      if (param.target) {
        const value = param.target.value;
        const name = param.target.getAttribute('name');
        const attribute = param.target.dataset.attribute;

        if (attribute) {
          menu.value = {
            data: {
              [attribute]: {
                [name]: value,
              },
            },
          };
        } else {
          menu.value = { [name]: value };
        }
      } else {
        menu.value = { data: merge(props.menu.attributes.data, param) };
      }
    };
    const getCustomerContact = async () => {
      const contact = await getContactByStatus('CUSTOMER');
      const newContact = {
        name: contact.attributes.company || (contact.attributes.first_name + ' ' + contact.attributes.last_name).trim(),
        street: contact.attributes.street || '',
        house_number: contact.attributes.house_number || '',
        address_supplement: contact.attributes.address_supplement || '',
        postal_code: contact.attributes.postal_code || '',
        city: contact.attributes.city || '',
        phone: contact.attributes.phone || '',
      };
      menu.value = {
        data: merge(props.menu.attributes.data, {
          contact: newContact,
        }),
      };
    };

    const inputRequired = (name) => {
      const route = useRoute();
      return route.meta.requiredAttributes.includes(name);
    };

    const { deleteLogo, uploadLogo } = useCover(menu);

    return {
      uploadLogo,
      deleteLogo,
      getCustomerContact,
      changeMenuData,
      inputRequired,
    };
  },
};
</script>
